import api from '../utils/api';

export interface UserStats {
  totalFiles: number;
  totalFolders: number;
  totalUploads: number;
  totalDownloads: number;
  totalShares: number;
  starredFiles: number;
  storageUsed: number;
  storageLimit: number;
}

export interface ActivityItem {
  id: string;
  type: 'upload' | 'download' | 'share' | 'delete' | 'edit' | 'star' | 'unstar' | 'create_folder';
  fileName: string;
  fileType?: string;
  timestamp: string;
  fileSize?: number;
  description: string;
}

export interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  organization?: string;
  bio?: string;
  location?: string;
  website?: string;
  avatar?: string;
}

export interface PasswordData {
  currentPassword: string;
  newPassword: string;
}

export interface UserSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  twoFactorAuth: boolean;
  darkMode: boolean;
  language: string;
  autoSave: boolean;
}

class ProfileService {
  // Get user profile
  async getProfile() {
    const response = await api.get('/auth/profile');
    return response.data;
  }

  // Update user profile
  async updateProfile(profileData: ProfileData) {
    const response = await api.put('/auth/profile', profileData);
    return response.data;
  }

  // Update password
  async updatePassword(passwordData: PasswordData) {
    const response = await api.put('/auth/password', passwordData);
    return response.data;
  }

  // Get user statistics
  async getUserStats(): Promise<UserStats> {
    const response = await api.get('/profile/stats');
    return response.data;
  }

  // Get recent activity
  async getRecentActivity(limit: number = 10): Promise<ActivityItem[]> {
    const response = await api.get(`/profile/activity?limit=${limit}`);
    return response.data.activities || [];
  }

  // Get all activity with pagination
  async getAllActivity(page: number = 1, limit: number = 20) {
    const response = await api.get(`/profile/activity?page=${page}&limit=${limit}`);
    return response.data;
  }

  // Update user settings
  async updateSettings(settings: Partial<UserSettings>) {
    const response = await api.put('/profile/settings', settings);
    return response.data;
  }

  // Get user settings
  async getSettings(): Promise<UserSettings> {
    const response = await api.get('/profile/settings');
    return response.data;
  }

  // Upload avatar
  async uploadAvatar(file: File) {
    const formData = new FormData();
    formData.append('avatar', file);
    
    const response = await api.post('/profile/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Delete avatar
  async deleteAvatar() {
    const response = await api.delete('/profile/avatar');
    return response.data;
  }

  // Get storage usage
  async getStorageUsage() {
    const response = await api.get('/storage/usage');
    return response.data;
  }

  // Get file type distribution
  async getFileTypeDistribution() {
    const response = await api.get('/profile/file-types');
    return response.data;
  }

  // Get upload history
  async getUploadHistory(page: number = 1, limit: number = 20) {
    const response = await api.get(`/profile/uploads?page=${page}&limit=${limit}`);
    return response.data;
  }

  // Get download history
  async getDownloadHistory(page: number = 1, limit: number = 20) {
    const response = await api.get(`/profile/downloads?page=${page}&limit=${limit}`);
    return response.data;
  }

  // Get share history
  async getShareHistory(page: number = 1, limit: number = 20) {
    const response = await api.get(`/profile/shares?page=${page}&limit=${limit}`);
    return response.data;
  }

  // Export profile data
  async exportProfileData() {
    const response = await api.get('/profile/export', {
      responseType: 'blob',
    });
    return response.data;
  }

  // Delete account
  async deleteAccount(password: string) {
    const response = await api.delete('/profile/account', {
      data: { password },
    });
    return response.data;
  }

  // Get account security info
  async getSecurityInfo() {
    const response = await api.get('/profile/security');
    return response.data;
  }

  // Enable two-factor authentication
  async enableTwoFactorAuth() {
    const response = await api.post('/profile/2fa/enable');
    return response.data;
  }

  // Disable two-factor authentication
  async disableTwoFactorAuth(password: string) {
    const response = await api.post('/profile/2fa/disable', { password });
    return response.data;
  }

  // Verify two-factor authentication
  async verifyTwoFactorAuth(code: string) {
    const response = await api.post('/profile/2fa/verify', { code });
    return response.data;
  }

  // Get login history
  async getLoginHistory(page: number = 1, limit: number = 20) {
    const response = await api.get(`/profile/login-history?page=${page}&limit=${limit}`);
    return response.data;
  }

  // Revoke session
  async revokeSession(sessionId: string) {
    const response = await api.delete(`/profile/sessions/${sessionId}`);
    return response.data;
  }

  // Get all active sessions
  async getActiveSessions() {
    const response = await api.get('/profile/sessions');
    return response.data;
  }

  // Get account analytics
  async getAccountAnalytics(timeRange: 'week' | 'month' | 'year' = 'month') {
    const response = await api.get(`/profile/analytics?range=${timeRange}`);
    return response.data;
  }

  // Get storage trends
  async getStorageTrends(timeRange: 'week' | 'month' | 'year' = 'month') {
    const response = await api.get(`/profile/storage-trends?range=${timeRange}`);
    return response.data;
  }

  // Get file activity summary
  async getFileActivitySummary() {
    const response = await api.get('/profile/file-activity');
    return response.data;
  }

  // Get collaboration stats
  async getCollaborationStats() {
    const response = await api.get('/profile/collaboration');
    return response.data;
  }

  // Get notification preferences
  async getNotificationPreferences() {
    const response = await api.get('/profile/notifications');
    return response.data;
  }

  // Update notification preferences
  async updateNotificationPreferences(preferences: any) {
    const response = await api.put('/profile/notifications', preferences);
    return response.data;
  }

  // Test email notification
  async testEmailNotification() {
    const response = await api.post('/profile/notifications/test-email');
    return response.data;
  }

  // Get account limits
  async getAccountLimits() {
    const response = await api.get('/profile/limits');
    return response.data;
  }

  // Request account upgrade
  async requestAccountUpgrade(plan: string) {
    const response = await api.post('/profile/upgrade', { plan });
    return response.data;
  }

  // Get billing information
  async getBillingInfo() {
    const response = await api.get('/profile/billing');
    return response.data;
  }

  // Update billing information
  async updateBillingInfo(billingData: any) {
    const response = await api.put('/profile/billing', billingData);
    return response.data;
  }

  // Get usage reports
  async getUsageReports(timeRange: 'week' | 'month' | 'year' = 'month') {
    const response = await api.get(`/profile/usage-reports?range=${timeRange}`);
    return response.data;
  }
}

const profileService = new ProfileService();
export default profileService; 